import { defaultBookshelfGroupId } from "@shared/bookshelf";
import type { BookSource, BookshelfViewItem, ContentKind, SearchResult } from "../api";
import type { ReaderSessionTarget } from "./reader-session";

export type ReadableBookItem = SearchResult | BookshelfViewItem;

export interface ReaderOpenRequest {
  userId: string;
  bookId: string;
  title: string;
  kind: ContentKind;
  source: BookSource;
  chapterId?: string;
  initialChapterIndex?: number;
  initialParagraphIndex?: number;
  initialScrollRatio?: number;
  openedFromSearch?: boolean;
  preserveCurrentOnError?: boolean;
  shouldNavigate: boolean;
}

export interface ReaderTargetRef {
  bookId: string;
  sourceId: string;
  sourceBookId: string;
}

export interface ReaderChapterListItem {
  id: string;
  title: string;
  index: number;
}

export interface ReaderChapterViewport {
  chapterId: string;
  top: number;
  bottom: number;
}

export interface ReaderParagraphViewport {
  index: number;
  top: number;
  bottom: number;
}

export function isBookshelfViewItem(item: ReadableBookItem): item is BookshelfViewItem {
  return "item" in item;
}

export function getSelectedBookSource(
  bookId: string,
  bookSources: BookSource[],
  selectedSourceByBook: Record<string, string>,
  fallbackSourceId?: string,
): BookSource | undefined {
  const selectedSourceId = selectedSourceByBook[bookId] ?? fallbackSourceId;
  return bookSources.find(source => source.sourceId === selectedSourceId) ?? bookSources[0];
}

export function createReaderOpenRequest({
  userId,
  item,
  selectedSourceByBook,
  chapterId,
}: {
  userId: string;
  item: ReadableBookItem;
  selectedSourceByBook: Record<string, string>;
  chapterId?: string;
}): ReaderOpenRequest | null {
  const bookshelfItem = isBookshelfViewItem(item) ? item : null;
  const explicitChapterId = chapterId !== undefined;
  const source = getSelectedBookSource(
    item.book.id,
    item.sources,
    selectedSourceByBook,
    bookshelfItem?.item.activeSourceId,
  );

  if (!source) return null;

  const savedProgress = bookshelfItem?.progress;
  const explicitChapterTarget = chapterId !== undefined;
  const sameProgressSource = !savedProgress?.sourceId || savedProgress.sourceId === source.sourceId;

  return {
    userId,
    bookId: item.book.id,
    title: item.book.title,
    kind: item.book.kind as ContentKind,
    source,
    chapterId: explicitChapterTarget ? chapterId : sameProgressSource ? savedProgress?.chapterId : undefined,
    initialChapterIndex: explicitChapterTarget ? undefined : sameProgressSource ? undefined : savedProgress?.chapterIndex,
    initialParagraphIndex: explicitChapterTarget ? 0 : savedProgress?.paragraphIndex,
    initialScrollRatio: explicitChapterTarget ? 0 : savedProgress?.scrollRatio,
    openedFromSearch: !bookshelfItem,
    shouldNavigate: true,
  };
}

export function findReadableBookItem(
  target: ReaderTargetRef,
  bookshelf: BookshelfViewItem[],
  results: SearchResult[],
): ReadableBookItem | null {
  return bookshelf.find(item => item.book.id === target.bookId) ?? results.find(item => item.book.id === target.bookId) ?? null;
}

export function getAdjacentChapter(chapters: ReaderChapterListItem[], currentChapterId: string, offset: -1 | 1): ReaderChapterListItem | null {
  const currentIndex = chapters.findIndex(chapter => chapter.id === currentChapterId);
  if (currentIndex < 0) return null;
  return chapters[currentIndex + offset] ?? null;
}

export function getNextAppendChapter(chapters: ReaderChapterListItem[], displayedChapterIds: string[]): ReaderChapterListItem | null {
  const lastDisplayedChapterId = displayedChapterIds.at(-1);
  if (!lastDisplayedChapterId) return chapters[0] ?? null;
  return getAdjacentChapter(chapters, lastDisplayedChapterId, 1);
}

export function pickChapterIdByIndex(chapters: ReaderChapterListItem[], chapterIndex?: number): string | undefined {
  if (chapterIndex === undefined || chapters.length === 0) return undefined;
  const boundedIndex = Math.max(0, Math.min(chapters.length - 1, chapterIndex));
  return chapters[boundedIndex]?.id;
}

export function pickReadingProgressChapter(viewports: ReaderChapterViewport[], viewportAnchor: number): string | null {
  const visibleChapters = viewports.filter(chapter => chapter.top <= viewportAnchor && chapter.bottom > 0);
  const anchoredChapter = visibleChapters.at(-1);

  if (anchoredChapter) return anchoredChapter.chapterId;
  return viewports.find(chapter => chapter.top >= 0)?.chapterId ?? viewports.at(-1)?.chapterId ?? null;
}

export function pickReadingParagraphIndex(viewports: ReaderParagraphViewport[], viewportAnchor: number): number | undefined {
  const visibleParagraphs = viewports.filter(paragraph => paragraph.top <= viewportAnchor && paragraph.bottom > 0);
  const anchoredParagraph = visibleParagraphs.at(-1);

  if (anchoredParagraph) return anchoredParagraph.index;
  return viewports.find(paragraph => paragraph.top >= 0)?.index ?? viewports.at(-1)?.index;
}

export function filterBookshelfByKeyword(items: BookshelfViewItem[], keyword: string): BookshelfViewItem[] {
  const normalizedKeyword = normalizeKeyword(keyword);
  if (!normalizedKeyword) return items;

  return items.filter(item => {
    const title = normalizeKeyword(item.book.title);
    const author = normalizeKeyword(item.book.author ?? "");
    return title.includes(normalizedKeyword) || author.includes(normalizedKeyword);
  });
}

export function filterBookshelfByGroup(items: BookshelfViewItem[], groupId: string): BookshelfViewItem[] {
  return items.filter(item => getBookshelfGroupId(item) === groupId);
}

export function getBookshelfGroupId(item: BookshelfViewItem): string {
  return item.item.groupId ?? defaultBookshelfGroupId;
}

export function sortBookshelfView(items: BookshelfViewItem[]): BookshelfViewItem[] {
  return [...items].sort((left, right) => {
    const rightTimestamp = Date.parse(right.progress?.updatedAt ?? right.item.updatedAt ?? "");
    const leftTimestamp = Date.parse(left.progress?.updatedAt ?? left.item.updatedAt ?? "");

    return normalizeTimestamp(rightTimestamp) - normalizeTimestamp(leftTimestamp);
  });
}

export function getBookshelfLatestChapterLabel(item: BookshelfViewItem, selectedSourceByBook: Record<string, string>): string {
  const source = getSelectedBookSource(item.book.id, item.sources, selectedSourceByBook, item.item.activeSourceId);
  return source?.latestChapterTitle ?? "未知最新章";
}

export function getBookshelfUnreadCount(item: BookshelfViewItem, selectedSourceByBook: Record<string, string>): number | null {
  const source = getSelectedBookSource(item.book.id, item.sources, selectedSourceByBook, item.item.activeSourceId);
  if (!source?.chapterCount || !item.progress) return null;

  return Math.max(0, source.chapterCount - (item.progress.chapterIndex + 1));
}

export type RecentReadingAction =
  | {
      type: "open";
      item: BookshelfViewItem;
      source: BookSource;
    }
  | {
      type: "search";
      keyword: string;
      kind: ContentKind;
    };

export function resolveRecentReadingAction(recentReading: ReaderSessionTarget, bookshelf: BookshelfViewItem[]): RecentReadingAction {
  const shelfItem = bookshelf.find(item => item.book.id === recentReading.bookId);

  if (!shelfItem) {
    return {
      type: "search",
      keyword: recentReading.title,
      kind: recentReading.kind,
    };
  }

  const source =
    shelfItem.sources.find(item => item.sourceId === recentReading.sourceId && item.sourceBookId === recentReading.sourceBookId) ??
    getSelectedBookSource(shelfItem.book.id, shelfItem.sources, {}, shelfItem.item.activeSourceId);

  if (!source) {
    return {
      type: "search",
      keyword: recentReading.title,
      kind: recentReading.kind,
    };
  }

  return {
    type: "open",
    item: shelfItem,
    source,
  };
}

function normalizeKeyword(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/\s+/g, "");
}

function normalizeTimestamp(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
